/**
 * StrategicAnalysisPage - Strategic analysis dashboard with session history.
 *
 * Layout:
 * - Left: Sidebar with navigation + strategic session history
 * - Main: Form (idle), ProcessBar + LogicTree (researching),
 *         DecisionSheet + FollowUpChat (done)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useStrategicStream, type StrategicStreamingState } from '@/hooks/useStrategicStream';
import { useResearchSessions } from '@/hooks/useResearchSessions';
import { useCredits } from '@/hooks/useCredits';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { Sidebar } from '@/components/layout/Sidebar';
import { ResearchSidebarContent } from '@/components/verdict/ResearchSidebarContent';
import { StrategicAnalysisForm, type StrategicAnalysisType } from '@/components/strategic/StrategicAnalysisForm';
import { StrategicLogicTree } from '@/components/verdict/StrategicLogicTree';
import { StrategicDecisionSheet } from '@/components/verdict/StrategicDecisionSheet';
import { ProcessBar } from '@/components/verdict/ProcessBar';
import { FollowUpChat } from '@/components/verdict/FollowUpChat';
import { getStrategicProcessMetadata } from '@/utils/processBarMetadata';
import { restoreStrategicState } from '@/utils/restoreState';
import { Alert } from '@/components/ui/Alert';
import { InsufficientCreditsDialog, CreditConsumedToast } from '@/components/credits';
import { INTENSITY_CREDIT_THRESHOLDS } from '@/types';
import type { StrategicIntensity } from '@/hooks/useStrategicStream';
import type { ResearchIntensity, ResearchMode } from '@/types';

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function buildTopic(
  subject: string,
  analysisType: StrategicAnalysisType,
): string {
  switch (analysisType) {
    case 'product_market_fit':
      return `Analyze product-market fit for ${subject}. Evaluate demand signals, target market alignment, value proposition clarity, and adoption potential.`;
    case 'competitive_positioning':
      return `Analyze competitive positioning of ${subject}. Map the competitive landscape, identify differentiation levers, assess moat strength, and evaluate defensibility.`;
    case 'go_to_market':
      return `Evaluate go-to-market strategy for ${subject}. Assess channels, timing, pricing strategy, target segments, and distribution approach.`;
    case 'swot':
      return `Conduct a SWOT analysis of ${subject}. Systematically evaluate strengths, weaknesses, opportunities, and threats with evidence.`;
    case 'general_strategic':
    default:
      return `Strategic analysis of ${subject}`;
  }
}

function inferAnalysisType(subject: string): StrategicAnalysisType {
  const lower = subject.toLowerCase();
  if (lower.includes('product-market fit') || lower.includes('product market fit') || lower.includes('pmf')) {
    return 'product_market_fit';
  }
  if (lower.includes('competitive') || lower.includes(' vs ') || lower.includes('versus') || lower.includes('landscape')) {
    return 'competitive_positioning';
  }
  if (lower.includes('go-to-market') || lower.includes('go to market') || lower.includes('gtm') || lower.includes('launch')) {
    return 'go_to_market';
  }
  if (lower.includes('swot') || lower.includes('strengths and weaknesses')) {
    return 'swot';
  }
  return 'general_strategic';
}

type UIState = 'idle' | 'researching' | 'conversation';

// --------------------------------------------------------------------------
// Page Component
// --------------------------------------------------------------------------
export function StrategicAnalysisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSubject = searchParams.get('subject') || '';
  const autoStart = searchParams.get('autoStart') === '1';
  const { user } = useAuth();
  const isAuthenticated = !!user;

  // ---- UI state ----
  const [uiState, setUIState] = useState<UIState>('idle');
  const [currentTopic, setCurrentTopic] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const userManuallyToggled = useRef(false);
  const isMobile = useIsMobile();
  const autoStartConsumed = useRef(false);
  const hasSavedRef = useRef(false);

  // ---- Session management (strategic sessions) ----
  const {
    sessions,
    currentSession,
    isLoading: sessionsLoading,
    isLoadingSession,
    pendingSessionId,
    createSession,
    selectSession,
    addMessage,
    archiveSession,
    clearCurrentSession,
  } = useResearchSessions('strategic');

  // ---- Credit system ----
  const { balance, isUnlimited, packs, fetchPacks, purchaseCredits, hasSufficientCredits, refreshBalance } = useCredits();
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [insufficientCreditsRequired, setInsufficientCreditsRequired] = useState(0);
  const preAnalysisBalanceRef = useRef<number | null>(null);
  const [creditToast, setCreditToast] = useState<{ creditsUsed: number; newBalance: number } | null>(null);

  // ---- Restored state (from session selection) ----
  const [restoredState, setRestoredState] = useState<StrategicStreamingState | null>(null);

  // ---- Follow-up chat state ----
  const [chatExpanded, setChatExpanded] = useState(false);
  const [sessionMessages, setSessionMessages] = useState<Array<{
    id: string;
    role: 'user' | 'council' | 'system';
    content: string;
    type: 'query' | 'verdict' | 'followup' | 'chat';
    confidence?: 'high' | 'medium' | 'low';
    suggestResearch?: boolean;
    createdAt: string;
  }>>([]);

  // Fetch packs on mount for purchase modals
  useEffect(() => { if (isAuthenticated) fetchPacks(); }, [isAuthenticated, fetchPacks]);

  // ---- Strategic stream ----
  const {
    state: strategicState,
    startAnalysis,
    reset: resetAnalysis,
  } = useStrategicStream({
    onComplete: () => {
      setUIState('conversation');
      // Credit toast
      const prevBal = preAnalysisBalanceRef.current;
      refreshBalance().then((result) => {
        if (result && prevBal !== null && !isUnlimited) {
          const used = prevBal - result.credit_balance;
          if (used > 0) setCreditToast({ creditsUsed: used, newBalance: result.credit_balance });
        }
        preAnalysisBalanceRef.current = null;
      });
    },
    onError: () => {
      if (strategicState.insufficientCredits) {
        setInsufficientCreditsRequired(strategicState.insufficientCredits.required);
        setShowInsufficientCredits(true);
      }
      setUIState('idle');
    },
  });

  const isDone = strategicState.phase === 'concluded';

  // ---- Auto-collapse sidebar during research (desktop) ----
  useEffect(() => {
    if (isMobile) return;
    if (userManuallyToggled.current) return;
    if (uiState === 'researching') {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
  }, [uiState, isMobile]);

  useEffect(() => {
    if (uiState === 'idle') {
      userManuallyToggled.current = false;
    }
  }, [uiState]);

  // ---- Form submit handler ----
  const handleFormSubmit = useCallback(({ subject, analysisType, depth }: {
    subject: string;
    analysisType: StrategicAnalysisType;
    depth: StrategicIntensity;
  }) => {
    // Credit gate
    if (isAuthenticated && balance !== null && !isUnlimited) {
      const requiredCredits = INTENSITY_CREDIT_THRESHOLDS[depth as keyof typeof INTENSITY_CREDIT_THRESHOLDS] || INTENSITY_CREDIT_THRESHOLDS.fast;
      if (!hasSufficientCredits(requiredCredits)) {
        setInsufficientCreditsRequired(requiredCredits);
        setShowInsufficientCredits(true);
        return;
      }
    }

    // Snapshot balance for consumption toast
    if (balance !== null && !isUnlimited) {
      preAnalysisBalanceRef.current = balance;
    }

    const topic = buildTopic(subject, analysisType);
    setCurrentTopic(topic);
    setRestoredState(null);
    setSessionMessages([]);
    clearCurrentSession();
    setUIState('researching');

    setSearchParams({ subject });
    startAnalysis(topic, depth);
  }, [startAnalysis, setSearchParams, isAuthenticated, balance, isUnlimited, hasSufficientCredits, clearCurrentSession]);

  const handleNewAnalysis = useCallback(() => {
    resetAnalysis();
    setCurrentTopic('');
    setRestoredState(null);
    setSessionMessages([]);
    clearCurrentSession();
    setUIState('idle');
    setChatExpanded(false);
    setSearchParams({});
  }, [resetAnalysis, setSearchParams, clearCurrentSession]);

  // ---- Auto-start from URL params ----
  useEffect(() => {
    if (autoStart && initialSubject && !autoStartConsumed.current && strategicState.phase === 'idle') {
      autoStartConsumed.current = true;
      const analysisType = inferAnalysisType(initialSubject);
      const topic = buildTopic(initialSubject, analysisType);
      setCurrentTopic(topic);
      setUIState('researching');
      setSearchParams({ subject: initialSubject }, { replace: true });
      startAnalysis(topic, 'fast');
    }
  }, [autoStart, initialSubject, strategicState.phase, startAnalysis, setSearchParams]);

  // ---- Save to session on completion ----
  useEffect(() => {
    if (!isDone || !strategicState.deliverable || !isAuthenticated) return;
    if (hasSavedRef.current) return;
    hasSavedRef.current = true;

    const saveToSession = async () => {
      const sessionTitle = strategicState.topic.length > 100
        ? strategicState.topic.slice(0, 100) + '...'
        : strategicState.topic;

      const fullState = {
        version: 1,
        topic: strategicState.topic,
        intensity: strategicState.intensity,
        workstreams: strategicState.workstreams,
        conflicts: strategicState.conflicts,
        stressTestResults: strategicState.stressTestResults,
        debateArguments: strategicState.debateArguments,
        debateRounds: strategicState.debateRounds,
        mediatorConsensus: strategicState.mediatorConsensus,
        ghostDeck: strategicState.ghostDeck,
        competitorData: strategicState.competitorData,
        deliverable: strategicState.deliverable,
        startedAt: strategicState.startedAt,
        completedAt: strategicState.completedAt,
        totalCreditsUsed: strategicState.totalCreditsUsed,
        workstreamCount: strategicState.workstreamCount,
        totalAgents: strategicState.totalAgents,
      };

      let sessionId = currentSession?.id;
      if (!sessionId) {
        const newSession = await createSession(sessionTitle);
        sessionId = newSession?.id;
      }

      if (sessionId) {
        await addMessage(sessionId, currentTopic || strategicState.topic, 'query', 'user');

        const verdictPayload = JSON.stringify({
          verdict: `Strategic analysis completed: ${strategicState.topic}`,
          blueprint: { topic: strategicState.topic, mode: 'strategic' },
          full_state: fullState,
        });
        await addMessage(sessionId, verdictPayload, 'verdict', 'assistant');

        setSessionMessages([
          {
            id: `query-${Date.now()}`,
            role: 'user',
            content: currentTopic || strategicState.topic,
            type: 'query',
            createdAt: new Date().toISOString(),
          },
        ]);
      }

      // Also save to legacy strategic history endpoint
      fetch(`${API_BASE_URL}/api/v1/strategic/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          topic: strategicState.topic,
          intensity: strategicState.intensity,
          full_state: fullState,
          executive_summary: strategicState.deliverable?.executiveSummary?.slice(0, 500),
          recommendation: strategicState.deliverable?.killSheet?.recommendation?.slice(0, 500),
          confidence_level: strategicState.deliverable?.killSheet?.confidenceLevel,
        }),
      }).catch(() => {});
    };

    saveToSession().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone]);

  // Reset save guard when starting new analysis
  useEffect(() => {
    if (strategicState.phase === 'idle') hasSavedRef.current = false;
  }, [strategicState.phase]);

  // ---- Session selection - restore state ----
  const handleSelectSession = useCallback(async (sessionId: string) => {
    await selectSession(sessionId);
  }, [selectSession]);

  // When currentSession changes, restore its state
  useEffect(() => {
    if (!currentSession?.messages?.length) return;

    const verdictMsg = [...currentSession.messages]
      .reverse()
      .find(m => m.message_type === 'verdict');

    if (verdictMsg) {
      try {
        const parsed = JSON.parse(verdictMsg.content);
        if (parsed.full_state) {
          const restored = restoreStrategicState(parsed.full_state);
          setRestoredState(restored);
          setCurrentTopic(parsed.blueprint?.topic || restored.topic || 'Analysis');
          setUIState('conversation');

          const msgs = currentSession.messages
            .filter(m => m.message_type === 'followup' || m.message_type === 'query')
            .map(m => ({
              id: m.id,
              role: (m.role === 'assistant' ? 'council' : m.role) as 'user' | 'council' | 'system',
              content: m.content,
              type: m.message_type as 'query' | 'verdict' | 'followup' | 'chat',
              createdAt: m.created_at,
            }));
          setSessionMessages(msgs);
          return;
        }
      } catch {
        // Not JSON or missing full_state
      }
    }

    setUIState('idle');
  }, [currentSession]);

  // Handle follow-up research deeper
  const handleResearchDeeper = useCallback((query: string, _intensity: ResearchIntensity, _mode: ResearchMode) => {
    const analysisType = inferAnalysisType(query);
    const topic = buildTopic(query, analysisType);
    setCurrentTopic(topic);
    setRestoredState(null);
    setSessionMessages([]);
    clearCurrentSession();
    setUIState('researching');
    startAnalysis(topic, 'fast');
  }, [startAnalysis, clearCurrentSession]);

  // Get the effective state (live or restored)
  const effectiveState = restoredState || strategicState;
  const effectiveIsDone = restoredState ? true : isDone;

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => {
          userManuallyToggled.current = true;
          setSidebarCollapsed(prev => !prev);
        }}
      >
        {isAuthenticated && (
          <ResearchSidebarContent
            sessions={sessions}
            currentSessionId={currentSession?.id}
            onSelectSession={handleSelectSession}
            onNewSession={handleNewAnalysis}
            onArchiveSession={archiveSession}
            isLoading={sessionsLoading}
            pendingSessionId={pendingSessionId}
            isLoadingSession={isLoadingSession}
          />
        )}
      </Sidebar>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header spacing */}
        <div className="md:hidden h-16 shrink-0" />

        <div className="flex-1 overflow-y-auto">
          <div className={cn(
            'mx-auto px-4 py-6',
            uiState === 'idle' ? 'max-w-3xl' : 'max-w-5xl',
          )}>
            {/* Insufficient credits error from stream */}
            {strategicState.phase === 'error' && strategicState.insufficientCredits && (
              <Alert variant="warning" className="mb-4" onClose={() => resetAnalysis()}>
                Insufficient credits for this analysis. Need {strategicState.insufficientCredits.required} credits, you have {strategicState.insufficientCredits.available}.{' '}
                <button
                  onClick={() => { setInsufficientCreditsRequired(strategicState.insufficientCredits!.required); setShowInsufficientCredits(true); }}
                  className="underline font-medium hover:text-text-primary transition-colors"
                >
                  Get more credits
                </button>
              </Alert>
            )}

            {/* Idle State: Strategic Form */}
            {uiState === 'idle' && !isLoadingSession && (
              <div className="flex-1 flex flex-col items-center justify-center min-h-[60vh]">
                <StrategicAnalysisForm
                  onSubmit={handleFormSubmit}
                  isSubmitting={false}
                  disabled={false}
                  initialSubject={initialSubject}
                />
              </div>
            )}

            {/* Researching State: ProcessBar + StrategicLogicTree */}
            {uiState === 'researching' && !isLoadingSession && (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Target className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-text-primary">Strategic Analysis</h2>
                      <p className="text-xs text-text-muted truncate max-w-[400px]">{currentTopic}</p>
                    </div>
                  </div>
                </div>

                {strategicState.phase !== 'idle' && !strategicState.insufficientCredits && (
                  <ProcessBar
                    metadata={getStrategicProcessMetadata(strategicState)}
                    defaultExpanded={true}
                    animateCollapse={false}
                  >
                    <StrategicLogicTree
                      state={strategicState}
                      query={currentTopic}
                      className="w-full"
                    />
                  </ProcessBar>
                )}
              </div>
            )}

            {/* Conversation State: Collapsed ProcessBar + DecisionSheet + FollowUpChat */}
            {uiState === 'conversation' && !isLoadingSession && (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleNewAnalysis}
                      className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-light transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Target className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-text-primary">Strategic Analysis</h2>
                      <p className="text-xs text-text-muted">Analysis Complete</p>
                    </div>
                  </div>
                  <button
                    onClick={handleNewAnalysis}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    New Analysis
                  </button>
                </div>

                {/* Collapsed ProcessBar with LogicTree */}
                {effectiveState.deliverable && (
                  <>
                    <ProcessBar
                      metadata={getStrategicProcessMetadata(effectiveState)}
                      defaultExpanded={false}
                      animateCollapse={!restoredState}
                    >
                      <StrategicLogicTree
                        state={effectiveState}
                        query={currentTopic}
                        className="w-full"
                      />
                    </ProcessBar>

                    <StrategicDecisionSheet
                      deliverable={effectiveState.deliverable}
                      stressTestResults={effectiveState.stressTestResults}
                      topic={effectiveState.topic}
                      intensity={effectiveState.intensity}
                      workstreams={effectiveState.workstreams}
                      conflicts={effectiveState.conflicts}
                      debateArguments={effectiveState.debateArguments}
                      debateRounds={effectiveState.debateRounds}
                      mediatorConsensus={effectiveState.mediatorConsensus}
                      ghostDeck={effectiveState.ghostDeck}
                      competitorData={effectiveState.competitorData}
                      startedAt={effectiveState.startedAt}
                      completedAt={effectiveState.completedAt}
                      onNewResearch={handleNewAnalysis}
                    />
                  </>
                )}

                {/* Follow-up Chat */}
                {isAuthenticated && currentSession?.id && effectiveIsDone && (
                  <FollowUpChat
                    sessionId={currentSession.id}
                    initialMessages={sessionMessages}
                    onResearchDeeper={handleResearchDeeper}
                    isResearching={false}
                    creditBalance={balance}
                    isUnlimited={isUnlimited}
                    isExpanded={chatExpanded}
                    onToggleExpand={() => setChatExpanded(prev => !prev)}
                    className="mt-6"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Insufficient Credits Dialog */}
      <InsufficientCreditsDialog
        isOpen={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        currentBalance={balance ?? 0}
        requiredCredits={insufficientCreditsRequired}
        queryType="standard_research"
        packs={packs}
        onPurchase={purchaseCredits}
      />

      {/* Credit Consumed Toast */}
      {creditToast && (
        <CreditConsumedToast
          creditsUsed={creditToast.creditsUsed}
          newBalance={creditToast.newBalance}
          onDismiss={() => setCreditToast(null)}
        />
      )}
    </div>
  );
}
